import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError, BaseResponse } from '@utils/types/shared/base.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export interface CustomerBoSubscription {
  id: number;
  plan_id: number;
  sku: string;
  start_date: string;
  end_date: string;
  cancel_date: string;
  auto_renew: boolean;
  suspended: boolean;
  billing_channel: string;
}

export type CustomerBoSubscriptionResponse = BaseResponse<
  CustomerBoSubscription[]
>;

export const useGetCustomerBoSubscriptions = (customerId: number) => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const qString = (searchParams.q as string | undefined) || undefined;

  const fetchSubscriptions = async () => {
    const { customerBoSubscriptionsByCustomerId } = GET_URL_NEW({
      offset,
      limit,
      q: qString,
      sort: sortField,
      direction: sortDirection
    });

    const url = customerBoSubscriptionsByCustomerId(customerId);
    const { data } = await Axios.get<CustomerBoSubscriptionResponse>(url);
    return data;
  };

  return useQuery<CustomerBoSubscriptionResponse, AxiosError<BaseError>>(
    [
      'customerBoSubscriptions',
      customerId,
      offset,
      limit,
      qString,
      sortField,
      sortDirection
    ],
    fetchSubscriptions,
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      onError: error => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = error.response?.data as any;
        const msg =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          t('an-error-has-occurred');
        toast.error(String(msg));
      }
    }
  );
};
